// src/utils/senderReferral.js
import { doc, setDoc, getDoc, query, collection, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

function generateShareId(length = 10) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Create (or return existing) a sender-referral share for a courier.
 *
 * Stored in: senderReferrals/{shareId}
 * The share link routes to: /laheta/{shareId}
 *
 * When a new sender signs up or places an order via this link, the courierId
 * is attached to the order so the finder's fee can be attributed.
 *
 * @param {string} courierId  - uid of the courier generating the link
 * @param {object} opts
 * @param {string} [opts.organizationId] - optional, routes new users into an org
 * @returns {Promise<{ shareId: string, url: string, isNew: boolean }>}
 */
export async function getOrCreateSenderReferral(courierId, { organizationId = null } = {}) {
  // Check whether this courier already has a link
  const existing = await getSenderReferralByCourier(courierId);
  if (existing) {
    return { shareId: existing.shareId, url: getSenderReferralUrl(existing.shareId), isNew: false };
  }

  // Create a new one
  const shareId = generateShareId();
  const data = {
    shareId,
    courierId,
    organizationId,
    createdAtMs: Date.now(),
    usageCount: 0,       // incremented each time the link is opened (optional server-side)
    orderCount: 0,       // incremented each time an order is placed via this link
  };

  await setDoc(doc(db, 'senderReferrals', shareId), data);
  return { shareId, url: getSenderReferralUrl(shareId), isNew: true };
}

/**
 * Fetch an existing sender-referral doc by shareId.
 * Used on the /laheta/:shareId route to pre-fill referrer.
 */
export async function getSenderReferral(shareId) {
  try {
    const snap = await getDoc(doc(db, 'senderReferrals', shareId));
    return snap.exists() ? snap.data() : null;
  } catch (err) {
    console.error('Error fetching senderReferral:', err);
    return null;
  }
}

/**
 * Find the sender-referral doc that belongs to a given courier.
 */
export async function getSenderReferralByCourier(courierId) {
  try {
    const q = query(
      collection(db, 'senderReferrals'),
      where('courierId', '==', courierId)
    );
    const snap = await getDocs(q);
    if (snap.empty) return null;
    return snap.docs[0].data();
  } catch (err) {
    console.error('Error fetching senderReferral by courier:', err);
    return null;
  }
}

/**
 * Build the full shareable URL for a sender-referral shareId.
 */
export function getSenderReferralUrl(shareId) {
  return `${window.location.origin}/laheta/${shareId}`;
}